import type { Config } from 'tailwindcss';
import colors from 'tailwindcss/colors';

export default {
  darkMode: 'class',
  content: [
    './src/**/*.{js,jsx,ts,tsx}',
    // @witmetrics/unison-core holds the status/type COLORS maps as Tailwind
    // class-name strings (e.g. `bg-navy-2`). Scan the package so JIT generates
    // those classes — otherwise statuses whose color isn't also used in app
    // source (navy/yellow/pink) render with no background.
    './node_modules/@witmetrics/unison-core/dist/**/*.{js,mjs}',
  ],
  theme: {
    colors: {
      grey: colors.gray,
      slate: colors.slate,
    },
    extend: {
      backgroundImage: {
        'default-gradient': 'url(./../public/images/gradient-1_sm.png)',
        // Aurora accent gradient (production default = orange)
        grad: 'linear-gradient(135deg, #ff7a45, #ef4823)',
        'grad-dark': 'linear-gradient(135deg, #ff8a5b, #f5512c)',
        // Consonance Button tone gradients (primary variant). Literal-color
        // names; the semantic `tone` prop maps to these in the Button. Dark
        // values not yet specified in the handoff — reuse light in both modes.
        'grad-blue': 'linear-gradient(135deg, #5b8bf0, #2f5fd0)',
        'grad-green': 'linear-gradient(135deg, #25c05c, #12903f)',
        'grad-amber': 'linear-gradient(135deg, #f7b23e, #f2a012)',
        'grad-red': 'linear-gradient(135deg, #ff6b5f, #e23b32)',
        'aurora-mesh':
          // 'radial-gradient(1500px 1100px at -11% -38%,rgba(255,162,104,.96),transparent 62%),radial-gradient(1200px 960px at 71% 67%,rgba(152,196,240,.74),transparent 56%),radial-gradient(640px 540px at 21% 95%,rgba(214,168,224,.46),transparent 55%),radial-gradient(580px 500px at 78% 19%,rgba(255,178,206,.42),transparent 55%),linear-gradient(180deg,#eceef5,#e6eaf3)',
          'radial-gradient(1450px 860px at 10% -2%, rgba(255, 164, 101, 0.65), transparent 56%), radial-gradient(1200px 1400px at 71% -4%, rgb(140 226 198 / 57%), transparent 54%), radial-gradient(1200px 960px at 18% 66%, rgba(214, 160, 242, 0.74), transparent 56%), radial-gradient(1300px 980px at 86% 104%, rgba(138, 180, 255, 0.88), transparent 56%), radial-gradient(980px 800px at 56% 30%, rgba(255, 168, 196, 0.62), transparent 55%), linear-gradient(rgb(236, 238, 245), rgb(230, 234, 243))',
        'aurora-mesh-dark':
          'radial-gradient(1500px 1100px at -11% -38%,rgba(255,150,95,.48),transparent 62%),radial-gradient(1200px 960px at 71% 67%,rgba(120,172,236,.32),transparent 56%),radial-gradient(640px 540px at 21% 95%,rgba(186,140,214,.22),transparent 55%),radial-gradient(580px 500px at 78% 19%,rgba(236,150,186,.2),transparent 55%),linear-gradient(180deg,#191623,#13111b)',
        // The Case drawer surface = the overlay's own --meshT radials (corner +
        // center blobs, distinct from the full-page aurora-mesh) under a veil.
        'drawer-mesh':
          'linear-gradient(rgba(255,255,255,.62),rgba(255,255,255,.62)),radial-gradient(1150px 860px at -2% -2%,rgba(255,164,108,.85),transparent 56%),radial-gradient(1380px 1020px at 102% -2%,rgba(94,214,214,.25),transparent 58%),radial-gradient(1200px 960px at 18% 66%,rgba(214,160,242,.74),transparent 56%),radial-gradient(1300px 980px at 86% 104%,rgba(138,180,255,.69),transparent 56%),radial-gradient(980px 800px at 56% 30%,rgba(255,168,196,.62),transparent 55%),linear-gradient(rgba(255,255,255,.86),rgba(255,255,255,.86))',
        'drawer-mesh-dark':
          'linear-gradient(rgba(33,29,47,0.82),rgba(33,29,47,0.82)),radial-gradient(1150px 860px at -2% -2%,rgba(255,164,108,.4),transparent 64%),radial-gradient(1100px 820px at 102% -2%,rgba(96,196,216,.3),transparent 54%),radial-gradient(1200px 960px at 18% 66%,rgba(214,160,242,.36),transparent 56%),radial-gradient(1300px 980px at 86% 104%,rgba(138,180,255,.44),transparent 56%),radial-gradient(980px 800px at 56% 30%,rgba(255,168,196,.3),transparent 55%)',
      },
      fontFamily: {
        sans: ['Inter', 'ui-sans-serif', 'system-ui', 'sans-serif'],
        mono: ['"JetBrains Mono"', 'ui-monospace', 'monospace'],
      },
      borderRadius: {
        lg: '0.5rem',
        xl: '0.75rem',
        '2xl': '1rem',
        '3xl': '1.5rem',
      },
      boxShadow: {
        // card: '0px 3px 8px 0px hsla(265, 10%, 23%, 0.15)',
        card: '0px 4px 16px hsla(0, 0%, 0%, 0.04)',
        'pop-up': '0px 4px 10px 0px hsla(0,0%,0%,0.15)',
        dialog: '0px 6px 15px 0px hsla(0,0%,0%,0.2)',
        sidebar: '0px 4px 20px 0 hsla(0,0%,0%,10%)',
        header: '0px 1px 8px 0 hsla(0,0%,0%,10%)',
        orange: '0px 4px 12px hsla(16, 100%, 61%, 0.4)',
        'orange-card': '0px 6px 12px hsla(16, 100%, 71%, 0.25)',
        'orange-chip': '0px 3px 8px hsla(16 100% 70% / 0.5)',
        // Aurora frosted-glass overlay, card lift, and list-row hover
        glass:
          '0 32px 84px -28px rgba(70,60,90,.32), 0 2px 10px rgba(70,60,90,.06)',
        'glass-dark':
          '0 36px 90px -28px rgba(0,0,0,.7), 0 2px 10px rgba(0,0,0,.45)',
        'aurora-card':
          '0 1px 2px rgba(40,30,30,.04), 0 14px 34px -16px rgba(60,50,60,.12)',
        'row-hover': '0 12px 26px -16px rgba(241,90,43,.34)',
        'file-hover': '0 18px 38px -14px rgba(44,78,146,.5)',
      },
      transitionProperty: {
        width: 'width',
      },
      colors: {
        transparent: 'transparent',
        white: '#fff',
        black: '#1b2932',
        // 'site-background': '#f8f9fa',
        'site-background': '#fafafb',
        'dialog-backdrop': '#15181C',
        brand: {
          blue: '#002255',
          orange: '#ff9068',
          // Aurora accent (production default); a brand color, usable anywhere
          accent: '#f15a2b',
          'accent-dark': '#ff7a4d',
          // Alternate Aurora accents (optional accent selector); solids only —
          // exact matching gradients pulled from the prototype when that lands.
          'accent-blue': '#2e6bff',
          'accent-teal': '#0f9bb0',
          'accent-indigo': '#5a45e0',
        },
        blue: {
          // 1: '#6bc1f8',
          1: '#E1EFF9',
          2: '#10a2fd',
          3: '#357dd2',
          4: '#255fb0',
          ...colors.blue,
        },
        cyan: {
          1: '#DBF5F7',
          2: '#22d3ee',
          3: '#06b6d4',
          4: '#0e7490',
        },
        emerald: {
          // 1: '#a7f3d0',
          1: '#E4F9F0',
          2: '#6ee7b7',
          3: '#34d399',
          4: '#059669',
        },
        green: {
          1: '#b4f3cf',
          2: '#58bd85',
          3: '#4aa37f',
          4: '#3a8474',
        },
        lime: {
          1: '#d9f99d',
          2: '#bef264',
          3: '#a3e635',
          4: '#84cc16',
        },
        navy: {
          1: '#6c8fe0',
          2: '#234aa4',
          3: '#1a377a',
          4: '#0c2254',
        },
        orange: {
          1: '#ffd1b7',
          2: '#f97b19',
          3: '#ff6b36',
          4: '#cc4700',
          ...colors.orange,
        },
        pink: {
          // 1: '#fecdd3',
          1: '#FCE2E5',
          2: '#fb7195',
          3: '#f43f5e',
          4: '#e11d48',
        },
        purple: {
          1: '#869af4',
          2: '#5370f4',
          3: '#475ece',
          4: '#3449ae',
          ...colors.purple,
        },
        red: {
          1: '#feb3af',
          2: '#f2625a',
          3: '#df2a20',
          4: '#bd180f',
        },
        yellow: {
          1: '#fef08a',
          2: '#fde047',
          3: '#facc15',
          4: '#eab308',
        },
        grey: {
          1: '#fafafa',
          // 2: '#f8f9fc',
          2: '#f3f5f7',
          3: '#e2e6e9',
          4: '#cedfe8',
          5: '#a7c1d2',
          6: '#7e9eb5',
          7: '#496f88',
          8: '#2d4453',
          9: '#121b21',
        },
        // Aurora warm text neutrals (light-mode inks), lightest -> darkest.
        // Primary text = taupe-3, secondary = taupe-2, tertiary/muted = taupe-1.
        taupe: {
          1: '#938b80',
          2: '#5f5a52',
          3: '#1a1510',
        },
        // Aurora cool text neutrals (dark-mode inks), lightest -> darkest.
        // Dark primary = mauve-1, secondary = mauve-2, tertiary/muted = mauve-3.
        mauve: {
          1: '#f3eff8',
          2: '#bfb6cb',
          3: '#8b8299',
        },
      },
    },
  },
  plugins: [],
} satisfies Config;
